import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useForm, SubmitHandler } from 'react-hook-form'
import { client } from 'utils/api-client'
import { makeNumbersFarsi, makeNumbersLatin } from 'utils/farsi'
import { VerificationCodeForm } from '../LoginForms/VerificationCodeForm'
import { PasswordForm } from '../LoginForms/PasswordForm'
import { Helmet } from 'react-helmet-async'
import { FullPageSpinner } from 'ui/FullPageSpinner'

interface IFormInputs {
  username: string
  password: string
  verificationCode: number
}

export const Login = () => {
  const [option, setOption] = useState<string | null>(null)
  const [username, setUsername] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormInputs>()

  const location = useLocation()
  const from = location.state

  const onSubmitUsername: SubmitHandler<IFormInputs> = async (data) => {
    setIsLoading(true)
    setUsername(makeNumbersFarsi(data.username))
    try {
      const normalizedUsername = makeNumbersLatin(String(data.username))
      const payload = { ...data, username: normalizedUsername }
      const result = await client('oauth/option', { data: payload })

      setOption(result.option)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (option === 'verification_code' && username) {
      const normalizedCellphone = makeNumbersLatin(String(username))
      const data = {
        cellphone: normalizedCellphone,
        client_id: '',
      }
      client('phones-verification-codes', { data })
    }
  }, [option, username])

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (option === 'verification_code') {
    return <VerificationCodeForm username={username} from={from} />
  }

  if (option === 'password') {
    return <PasswordForm username={username} from={from} />
  }

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const transformedValue = makeNumbersFarsi(event.target.value)
    setUsername(transformedValue)
  }

  const registerWithTransform = {
    ...register('username', { required: true }),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      handleUsernameChange(e)
      register('username', { required: true }).onChange(e)
    },
  }

  return (
    <div className='my-12'>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          {' '}
          ورود به بوک استوری
        </title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <form
        className='max-w-lg w-full mt-5 mx-auto px-4 sm:px-6'
        onSubmit={handleSubmit(onSubmitUsername)}
      >
        <h1 className='text-base sm:text-lg md:text-xl text-center pb-2 sm:pb-2.5 mb-2 sm:mb-3 leading-relaxed'>
          لطفا شماره تلفن خود را وارد کنید
        </h1>
        <input
          autoFocus
          className='input-theme block box-border w-full rounded border border-solid py-3 px-4 mb-2.5 text-base sm:text-lg text-left'
          value={username}
          dir='ltr'
          {...registerWithTransform}
        />
        {errors.username && (
          <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
            {' '}
            نام کاربری الزامی است
          </p>
        )}
        <input
          className='w-full rounded text-white border-none mt-8 sm:mt-10 py-3 sm:py-4 px-4 text-base sm:text-lg md:text-xl font-bold tracking-wider active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value=' ادامه'
        />
      </form>
    </div>
  )
}
